import { createFileRoute } from "@tanstack/react-router";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { ImageField, InputField, SelectField } from "@/components/ui/form";
import { useSimpleForm } from "@/hooks/use-simple-form";
import { useTranslate } from "@/hooks/use-translate";
import { pick } from "@/lib/utils";
import useAppStore from "@/store";
import { Country, State } from "country-state-city";
import { ChevronLeft } from "lucide-react";
import { useEffect } from "react";

export const Route = createFileRoute("/(dasboard)/profile")({
  component: ProfilePage,
});

function ProfilePage() {
  const store = useAppStore();
  const { t } = useTranslate();

  const form = useSimpleForm({
    first_name: "",
    last_name: "",
    phone_number: "",
    region: "",
    country: "",
    city: "",
    address: "",
    image: { file: undefined, src: "" },
  });

  useEffect(() => {
    const user = store.auth.user;
    form.setValues((values: any) => {
      const newValues = {
        ...values,
        first_name: user?.first_name,
        last_name: user?.last_name,
        region: user?.location?.region,
        country: user?.location?.country,
        city: user?.location?.city,
        address: user?.location?.address,
      };

      if (newValues["country"] == undefined || newValues["country"] == "") {
        form.setValue("country", "CM");
        form.setValue("region", State.getStatesOfCountry("CM")[0]?.name);
      }

      return newValues;
    });

    if (user?.image?.url) {
      form.setValue("image", {
        file: undefined,
        src: user.image.url,
      });
    }
  }, [store.auth.session]);

  const submit = () => [
    store.user
      .update(store.auth.user?.id as string, form.getFormData())
      .then(() => {
        window.location.reload();
      })
      .catch(store.errors.catch),
  ];

  const changePassword = () => {
    store.errors.reset();
    store.user
      .changePassword(store.auth.user?.id as string, {
        _method: "PUT",
        ...pick(form.values, ["current_password", "new_password"]),
      })
      .then(() => {
        store.display.show("change_password_success");
        form.setValue("current_password", "");
        form.setValue("new_password", "");
      })
      .catch((error) => {
        store.errors.catch(error);
        window.scrollTo({
          top: 0,
        });
      });
  };

  return (
    <>
      <Alert
        autoClose={3000}
        message={t("Password changed with success")}
        name={"change_password_success"}
      />

      <section className="max-w-4xl mx-auto">
        <div>
          <button
            type="button"
            onClick={() => window.history.back()}
            className="py-2 inline-flex gap-2 items-center justify-center rounded-lg text-sm text-gray-500 hover:text-gray-900 hover:text-gray-900 hover:underline font-semibold"
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="">{t("Back")}</span>
          </button>
        </div>

        <div className="mt-4 flex justify-between mb-4">
          <h2 className="text-xl font-semibold">{t("Profile information")}</h2>
        </div>

        <>{store.errors.render()}</>

        <div className="bg-white p-6 rounded-md border-gray-200">
          <div>
            <div className="">
              <ImageField
                label={t("Profile Picture")}
                name="image"
                image={form.getImageValue("image")}
                onImageChange={form.handleImageChange("image")}
                error={store.errors.values.image}
                id="image"
                className="h-64 w-64 border-dashed"
              />
            </div>
            <div className="gap-4 mt-6 grid grid-cols-2">
              <InputField
                label={t("Firstname")}
                name="first_name"
                value={form.values.first_name}
                onChange={form.handleChange}
                error={store.errors.values.first_name}
              />
              <InputField
                label={t("Lastname")}
                name="last_name"
                value={form.values.last_name}
                onChange={form.handleChange}
                error={store.errors.values.last_name}
              />
              <SelectField
                label={t("Country")}
                name="country"
                value={form.values.country}
                onChange={form.handleChange}
                options={Country.getAllCountries().map((c) => ({
                  label: c.name,
                  value: c.isoCode,
                }))}
                required
              />

              <SelectField
                label={t("State/Region")}
                name="region"
                value={form.values.region}
                onChange={form.handleChange}
                options={
                  form.values.country
                    ? State.getStatesOfCountry(form.values.country).map(
                        (s) => ({
                          label: s.name,
                          value: s.isoCode,
                        })
                      )
                    : []
                }
                required
              />

              <InputField
                label={t("City")}
                name="city"
                value={form.values.city}
                onChange={form.handleChange}
                error={store.errors.values.city}
              />

              <InputField
                label={t("Street address")}
                name="address"
                value={form.values.address}
                onChange={form.handleChange}
                error={store.errors.values.address}
              />
            </div>
          </div>
          <Button className="mt-6 px-6 py-2" onClick={submit}>
            {t("Update profile informations")}
          </Button>
        </div>

        <div className="bg-white p-6 mt-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <InputField
              label={t("Old password")}
              name="current_password"
              type="password"
              canToggleType={true}
              value={form.values.current_password}
              onChange={form.handleChange}
              error={store.errors.values.current_password}
            />
            <InputField
              label={t("New password")}
              name="new_password"
              type="password"
              canToggleType={true}
              value={form.values.new_password}
              onChange={form.handleChange}
              error={store.errors.values.new_password}
            />
          </div>
          <Button className="mt-6 px-6 py-2" onClick={changePassword}>
            {t("Change password")}
          </Button>
        </div>
      </section>
    </>
  );
}
